import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import {
    Calendar,
    Syringe,
    Scissors,
    Stethoscope,
    Plus,
    Loader2,
    Clock,
    PawPrint,
    Store,
    CheckCircle2,
    Bell,
    Trash2,
    CheckCircle,
    Circle,
    CalendarIcon,
    X,
    AlertTriangle,
    ChevronLeft,
    ChevronRight,
    Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// ============================================================
// CONFIGURACIÓN DE TIPOS DE EVENTOS
// ============================================================
const TIPO_CONFIG = {
    veterinario: {
        icon: Stethoscope,
        bg: "bg-blue-50",
        border: "border-blue-400",
        text: "text-blue-700",
        badge: "bg-blue-100 text-blue-800",
        hover: "hover:border-blue-500",
    },
    vacuna: {
        icon: Syringe,
        bg: "bg-green-50",
        border: "border-green-400",
        text: "text-green-700",
        badge: "bg-green-100 text-green-800",
        hover: "hover:border-green-500",
    },
    peluqueria: {
        icon: Scissors,
        bg: "bg-pink-50",
        border: "border-pink-400",
        text: "text-pink-700",
        badge: "bg-pink-100 text-pink-800",
        hover: "hover:border-pink-500",
    },
    petshop: {
        icon: Store,
        bg: "bg-purple-50",
        border: "border-purple-400",
        text: "text-purple-700",
        badge: "bg-purple-100 text-purple-800",
        hover: "hover:border-purple-500",
    },
    recordatorio: {
        icon: Bell,
        bg: "bg-amber-50",
        border: "border-amber-400",
        text: "text-amber-700",
        badge: "bg-amber-100 text-amber-800",
        hover: "hover:border-amber-500",
    },
    otro: {
        icon: Calendar,
        bg: "bg-gray-50",
        border: "border-gray-400",
        text: "text-gray-700",
        badge: "bg-gray-100 text-gray-800",
        hover: "hover:border-gray-500",
    },
};

// ============================================================
// FUNCIONES AUXILIARES
// ============================================================
function formatearFecha(fecha) {
    if (!fecha) return "";
    return new Date(fecha).toLocaleDateString("es-AR", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

function formatearFechaInput(fecha) {
    if (!fecha) return "";
    return new Date(fecha).toISOString().split("T")[0];
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
export default function Agenda() {
    const { userId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [mascotas, setMascotas] = useState([]);
    const [selectedPetId, setSelectedPetId] = useState(null);
    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingEventos, setLoadingEventos] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        tipo_evento: "otro",
        titulo: "",
        fecha_evento: new Date().toISOString().split("T")[0],
        nota: "",
    });
    const [saving, setSaving] = useState(false);

    // Estado para filtros
    const [filtro, setFiltro] = useState("todos"); // 'todos', 'realizadas', 'pendientes'

    // Estados para modales personalizados
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [eventoAEliminar, setEventoAEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [eventoAReagendar, setEventoAReagendar] = useState(null);
    const [nuevaFecha, setNuevaFecha] = useState("");
    const [reagendando, setReagendando] = useState(false);

    // ----------------------------------------------------------
    // Cargar mascotas del usuario
    // ----------------------------------------------------------
    useEffect(() => {
        if (user?.id?.toString() !== userId) {
            navigate(`/consultas/${user.id}/agenda`, { replace: true });
            return;
        }
        const cargarMascotas = async () => {
            try {
                const res = await api.getUserCodes(userId);
                const petsData = res.data?.success ? res.data.data : [];
                const pets = petsData
                    .filter((item) => item.mascota && item.mascota.id)
                    .map((item) => ({
                        id: item.mascota.id,
                        nombre: item.mascota.nombre || "Sin nombre",
                        foto: item.mascota.urlImg,
                    }));
                setMascotas(pets);
                if (pets.length > 0) {
                    setSelectedPetId(pets[0].id);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        cargarMascotas();
    }, [userId, user, navigate]);

    // ----------------------------------------------------------
    // Cargar eventos de la mascota seleccionada
    // ----------------------------------------------------------
    const cargarEventos = async () => {
        if (!selectedPetId) {
            setEventos([]);
            return;
        }
        setLoadingEventos(true);
        try {
            const res = await api.getHistorialByMascota(selectedPetId);
            const data = res.data || [];
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => new Date(b.fecha_evento) - new Date(a.fecha_evento));
            setEventos(list);
        } catch (err) {
            console.error("Error cargando eventos:", err);
            setEventos([]);
        } finally {
            setLoadingEventos(false);
        }
    };

    useEffect(() => {
        cargarEventos();
    }, [selectedPetId]);

    // ----------------------------------------------------------
    // Agregar nuevo evento
    // ----------------------------------------------------------
    const handleAddEvent = async () => {
        if (!form.titulo.trim() || !form.fecha_evento) {
            alert("Completá el título y la fecha");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                id_mascota: selectedPetId,
                titulo: form.titulo.trim(),
                fecha_evento: form.fecha_evento,
                tipo_evento: form.tipo_evento,
                nota: form.nota.trim(),
            };
            const res = await api.createHistorial(payload);
            if (res.data?.success) {
                setShowForm(false);
                setForm({
                    tipo_evento: "otro",
                    titulo: "",
                    fecha_evento: new Date().toISOString().split("T")[0],
                    nota: "",
                });
                cargarEventos();
            } else {
                alert("Error al guardar: " + (res.data?.error || "Error desconocido"));
            }
        } catch (err) {
            console.error("Error al crear evento:", err.response?.data || err.message);
            alert("Error al crear evento: " + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    // ----------------------------------------------------------
    // Eliminar evento (con modal personalizado)
    // ----------------------------------------------------------
    const confirmarEliminar = (evento) => {
        setEventoAEliminar(evento);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!eventoAEliminar) return;
        setEliminando(true);
        try {
            await api.deleteHistorial(eventoAEliminar.id);
            setShowDeleteModal(false);
            setEventoAEliminar(null);
            cargarEventos();
        } catch (err) {
            console.error("Error al eliminar evento:", err);
            alert("Error al eliminar el evento");
        } finally {
            setEliminando(false);
        }
    };

    // ----------------------------------------------------------
    // Marcar como completada / pendiente
    // ----------------------------------------------------------
    const handleToggleCompletada = async (evento) => {
        const nuevaCompletada = evento.completada == 1 ? 0 : 1;
        try {
            await api.updateHistorial(evento.id, { completada: nuevaCompletada });
            cargarEventos();
        } catch (err) {
            console.error("Error al actualizar estado:", err);
            alert("Error al actualizar el estado del evento");
        }
    };

    // ----------------------------------------------------------
    // Reagendar evento (con modal personalizado)
    // ----------------------------------------------------------
    const abrirReagendar = (evento) => {
        setEventoAReagendar(evento);
        setNuevaFecha(formatearFechaInput(evento.fecha_evento));
        setShowRescheduleModal(true);
    };

    const handleReagendarConfirm = async () => {
        if (!eventoAReagendar || !nuevaFecha) {
            alert("Seleccioná una fecha válida");
            return;
        }
        setReagendando(true);
        try {
            await api.updateHistorial(eventoAReagendar.id, { fecha_evento: nuevaFecha });
            setShowRescheduleModal(false);
            setEventoAReagendar(null);
            setNuevaFecha("");
            cargarEventos();
        } catch (err) {
            console.error("Error al reagendar:", err);
            alert("Error al reagendar el evento");
        } finally {
            setReagendando(false);
        }
    };

    // ----------------------------------------------------------
    // Utilidad para obtener URL de foto
    // ----------------------------------------------------------
    const getPhotoUrl = (urlImg) => {
        if (!urlImg) return null;
        if (urlImg.startsWith("http")) return urlImg;
        const base = process.env.REACT_APP_API_URL || "http://localhost/api-smartpet";
        return `${base.replace(/\/$/, "")}/${urlImg.replace(/^\//, "")}`;
    };

    const petSeleccionado = mascotas.find((m) => m.id === selectedPetId);

    // ----------------------------------------------------------
    // Cálculo de resumen y filtrado
    // ----------------------------------------------------------
    const totalEventos = eventos.length;
    const completadas = eventos.filter((e) => e.completada == 1).length;
    const pendientes = eventos.filter((e) => e.completada == 0 || e.completada === null).length;

    let eventosFiltrados = eventos;
    if (filtro === "realizadas") {
        eventosFiltrados = eventos.filter((e) => e.completada == 1);
    } else if (filtro === "pendientes") {
        eventosFiltrados = eventos.filter((e) => e.completada == 0 || e.completada === null);
    }

    // ----------------------------------------------------------
    // RENDER
    // ----------------------------------------------------------
    if (loading) {
        return (
            <div className="min-h-screen bg-sp-light flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-sp-primary animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sp-light mt-16">
            {/* ========== HEADER ========== */}
            <section className="bg-gradient-to-br from-sp-dark to-sp-primary pt-6 pb-12 px-4 xs:px-5 relative overflow-hidden">
                <div className="max-w-[1100px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-white text-xl xs:text-2xl font-black flex items-center gap-2">
                            <Calendar className="w-7 h-7" /> Agenda
                        </h1>
                        <p className="text-white/70 text-sm font-semibold mt-1">
                            {mascotas.length === 0
                                ? 'Vincular una mascota para empezar'
                                : `Gestioná los eventos de ${mascotas.length} mascota${mascotas.length > 1 ? 's' : ''}`}
                        </p>
                    </div>
                    {mascotas.length > 0 && selectedPetId && (
                        <button
                            onClick={() => setShowForm(true)}
                            className="bg-sp-cta text-white px-5 py-3 rounded-full font-extrabold text-sm shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 active:scale-95 transition flex items-center gap-2"
                        >
                            <Plus size={18} /> Agregar evento
                        </button>
                    )}
                </div>
                <div
                    className="absolute -bottom-0.5 left-0 right-0 h-8 bg-sp-light"
                    style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }}
                />
            </section>

            {/* ========== SELECTOR DE MASCOTA ========== */}
            {mascotas.length > 0 && (
                <div className="max-w-[1100px] mx-auto px-4 xs:px-5 py-4 flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-sp-primary/20 scrollbar-track-transparent">
                    {mascotas.map((pet) => (
                        <button
                            key={pet.id}
                            onClick={() => setSelectedPetId(pet.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition font-bold text-sm whitespace-nowrap ${selectedPetId === pet.id
                                ? "bg-sp-primary text-white shadow-md"
                                : "bg-white text-gray-700 shadow-sm hover:bg-gray-50"
                                }`}
                        >
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                                {pet.foto ? (
                                    <img src={getPhotoUrl(pet.foto)} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <PawPrint size={16} className="text-gray-500" />
                                )}
                            </div>
                            <span className="truncate max-w-[120px]">{pet.nombre}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* ========== CONTENIDO PRINCIPAL ========== */}
            <div className="max-w-[1100px] mx-auto px-4 xs:px-5 pb-10">
                {mascotas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-3xl shadow-sp">
                        <Calendar className="w-16 h-16 text-sp-primary/30 mb-4" />
                        <h3 className="text-sp-text font-black text-lg mb-2">Aún no tenés mascotas vinculadas</h3>
                        <p className="text-sp-muted font-semibold mb-6">Vinculá una chapita para empezar a gestionar eventos</p>
                        <button
                            onClick={() => navigate(`/consultas/${userId}/mismascotas`)}
                            className="bg-sp-cta text-white px-5 py-2.5 rounded-full font-extrabold shadow-cta hover:-translate-y-0.5 transition flex items-center gap-2"
                        >
                            <Plus size={18} /> Ir a Mis mascotas
                        </button>
                    </div>
                ) : (
                    <>
                        {/* ===== RESUMEN ===== */}
                        <div className="grid grid-cols-3 gap-3 xs:gap-4 mb-6">
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-gray-100">
                                <p className="text-2xl font-black text-gray-800">{totalEventos}</p>
                                <p className="text-xs text-gray-500 font-semibold">Totales</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-green-100">
                                <p className="text-2xl font-black text-green-600">{completadas}</p>
                                <p className="text-xs text-gray-500 font-semibold">Realizadas</p>
                            </div>
                            <div className="bg-white rounded-xl p-3 text-center shadow-sm border border-amber-100">
                                <p className="text-2xl font-black text-amber-600">{pendientes}</p>
                                <p className="text-xs text-gray-500 font-semibold">Pendientes</p>
                            </div>
                        </div>

                        {/* ===== FILTROS ===== */}
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setFiltro("todos")}
                                className={`flex-1 py-2 px-4 rounded-xl font-bold text-sm transition ${filtro === "todos"
                                    ? "bg-indigo-600 text-white shadow-md"
                                    : "bg-white text-gray-700 hover:bg-gray-50 shadow-sm"
                                    }`}
                            >
                                Todos
                            </button>
                            <button
                                onClick={() => setFiltro("realizadas")}
                                className={`flex-1 py-2 px-4 rounded-xl font-bold text-sm transition ${filtro === "realizadas"
                                    ? "bg-green-600 text-white shadow-md"
                                    : "bg-white text-gray-700 hover:bg-gray-50 shadow-sm"
                                    }`}
                            >
                                <CheckCircle2 size={16} className="inline mr-1" /> Realizadas
                            </button>
                            <button
                                onClick={() => setFiltro("pendientes")}
                                className={`flex-1 py-2 px-4 rounded-xl font-bold text-sm transition ${filtro === "pendientes"
                                    ? "bg-amber-600 text-white shadow-md"
                                    : "bg-white text-gray-700 hover:bg-gray-50 shadow-sm"
                                    }`}
                            >
                                <Clock size={16} className="inline mr-1" /> Pendientes
                            </button>
                        </div>

                        {/* ===== LISTA DE EVENTOS ===== */}
                        {loadingEventos ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="w-10 h-10 border-4 border-sp-primary border-t-transparent rounded-full animate-spin mb-4" />
                                <p className="text-sp-muted font-semibold">Cargando eventos...</p>
                            </div>
                        ) : eventosFiltrados.length === 0 ? (
                            <div className="bg-white rounded-2xl p-10 text-center shadow-sm border border-dashed border-gray-300">
                                {filtro === "todos" && (
                                    <>
                                        <Calendar className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500 font-semibold">No hay eventos para esta mascota</p>
                                        <button
                                            onClick={() => setShowForm(true)}
                                            className="mt-4 text-indigo-600 font-bold text-sm hover:underline"
                                        >
                                            + Agregar primer evento
                                        </button>
                                    </>
                                )}
                                {filtro === "realizadas" && (
                                    <>
                                        <CheckCircle2 className="w-14 h-14 text-green-300 mx-auto mb-4" />
                                        <p className="text-gray-500 font-semibold">No hay eventos realizados</p>
                                    </>
                                )}
                                {filtro === "pendientes" && (
                                    <>
                                        <Clock className="w-14 h-14 text-amber-300 mx-auto mb-4" />
                                        <p className="text-gray-500 font-semibold">No hay eventos pendientes</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {eventosFiltrados.map((ev) => {
                                    const tipo = ev.tipo_evento || "otro";
                                    const config = TIPO_CONFIG[tipo] || TIPO_CONFIG.otro;
                                    const Icon = config.icon;
                                    const isCompletada = ev.completada == 1;

                                    return (
                                        <motion.div
                                            key={ev.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`bg-white rounded-xl shadow-sm p-4 border-l-4 ${config.border} ${config.hover} hover:shadow-md transition ${isCompletada ? "opacity-75" : ""
                                                }`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={`p-2 rounded-full ${config.bg} ${config.text}`}>
                                                    <Icon size={20} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                                        <h3 className="font-bold text-gray-800 truncate">
                                                            {ev.titulo}
                                                        </h3>
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            <span className="text-xs text-gray-400 flex items-center gap-1">
                                                                <Clock size={12} />
                                                                {formatearFecha(ev.fecha_evento)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {ev.nota && (
                                                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ev.nota}</p>
                                                    )}
                                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${config.badge}`}>
                                                            {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                                                        </span>
                                                        {isCompletada && (
                                                            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                                                <CheckCircle2 size={12} /> Realizada
                                                            </span>
                                                        )}
                                                        {ev.recordatorio == 1 && (
                                                            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                                                <Bell size={12} /> Recordatorio
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Botones de acción (abajo) */}
                                            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                                                <button
                                                    onClick={() => handleToggleCompletada(ev)}
                                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition ${isCompletada
                                                        ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                        : "bg-green-50 text-green-700 hover:bg-green-100"
                                                        }`}
                                                >
                                                    {isCompletada ? (
                                                        <>
                                                            <Circle size={14} /> Marcar pendiente
                                                        </>
                                                    ) : (
                                                        <>
                                                            <CheckCircle size={14} /> Marcar realizada
                                                        </>
                                                    )}
                                                </button>

                                                <button
                                                    onClick={() => abrirReagendar(ev)}
                                                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                                                >
                                                    <CalendarIcon size={14} /> Reagendar
                                                </button>

                                                <button
                                                    onClick={() => confirmarEliminar(ev)}
                                                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-red-50 text-red-600 hover:bg-red-100 transition"
                                                >
                                                    <Trash2 size={14} /> Eliminar
                                                </button>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ========== MODAL PARA AGREGAR EVENTO ========== */}
            {showForm && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setShowForm(false)}
                >
                    <div
                        className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-black text-gray-800 flex items-center gap-2">
                                <Plus size={20} className="text-indigo-600" /> Nuevo evento
                            </h2>
                            <button
                                onClick={() => setShowForm(false)}
                                className="p-1 rounded-full hover:bg-gray-100 transition"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            {/* Tipo */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo</label>
                                <select
                                    value={form.tipo_evento}
                                    onChange={(e) => setForm({ ...form, tipo_evento: e.target.value })}
                                    className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                >
                                    {Object.keys(TIPO_CONFIG).map((key) => (
                                        <option key={key} value={key}>
                                            {key.charAt(0).toUpperCase() + key.slice(1)}
                                        </option>
                                    ))}
                                </select>
                                <div className="mt-1 flex items-center gap-2">
                                    {(() => {
                                        const config = TIPO_CONFIG[form.tipo_evento] || TIPO_CONFIG.otro;
                                        const Icon = config.icon;
                                        return (
                                            <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${config.badge}`}>
                                                <Icon size={12} /> {form.tipo_evento.charAt(0).toUpperCase() + form.tipo_evento.slice(1)}
                                            </span>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Título */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Título</label>
                                <input
                                    type="text"
                                    value={form.titulo}
                                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                                    className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                    placeholder="Ej: Vacuna antirrábica"
                                />
                            </div>

                            {/* Fecha */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Fecha</label>
                                <input
                                    type="date"
                                    value={form.fecha_evento}
                                    onChange={(e) => setForm({ ...form, fecha_evento: e.target.value })}
                                    className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                />
                            </div>

                            {/* Notas */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Notas</label>
                                <textarea
                                    value={form.nota}
                                    onChange={(e) => setForm({ ...form, nota: e.target.value })}
                                    className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                                    rows={3}
                                    placeholder="Detalles adicionales..."
                                />
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="px-4 py-2 text-gray-500 font-bold text-sm hover:text-gray-700 transition"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleAddEvent}
                                    disabled={saving}
                                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full font-bold text-sm flex items-center gap-2 transition disabled:opacity-50"
                                >
                                    {saving ? (
                                        <>
                                            <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <Plus size={16} /> Agregar
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== MODAL CONFIRMAR ELIMINACIÓN ========== */}
            <AnimatePresence>
                {showDeleteModal && eventoAEliminar && (
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <AlertTriangle size={32} className="text-red-600" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">Eliminar evento</h3>
                                <p className="text-gray-500 text-sm mb-6">
                                    ¿Estás seguro que querés eliminar el evento <strong>"{eventoAEliminar.titulo}"</strong>?
                                    <br />
                                    Esta acción no se puede deshacer.
                                </p>
                                <div className="flex justify-center gap-3">
                                    <button
                                        onClick={() => setShowDeleteModal(false)}
                                        className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full font-bold text-sm transition"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleDeleteConfirm}
                                        disabled={eliminando}
                                        className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold text-sm flex items-center gap-2 transition disabled:opacity-50"
                                    >
                                        {eliminando ? (
                                            <>
                                                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                                Eliminando...
                                            </>
                                        ) : (
                                            <>
                                                <Trash2 size={16} /> Eliminar
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ========== MODAL REAGENDAR ========== */}
            <AnimatePresence>
                {showRescheduleModal && eventoAReagendar && (
                    <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowRescheduleModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-center">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CalendarIcon size={32} className="text-blue-600" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">Reagendar evento</h3>
                                <p className="text-gray-500 text-sm mb-4">
                                    Cambiá la fecha del evento <strong>"{eventoAReagendar.titulo}"</strong>
                                </p>
                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1 text-left">
                                        Nueva fecha
                                    </label>
                                    <input
                                        type="date"
                                        value={nuevaFecha}
                                        onChange={(e) => setNuevaFecha(e.target.value)}
                                        className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                    />
                                </div>
                                <div className="flex justify-center gap-3">
                                    <button
                                        onClick={() => setShowRescheduleModal(false)}
                                        className="px-5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full font-bold text-sm transition"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleReagendarConfirm}
                                        disabled={reagendando}
                                        className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-bold text-sm flex items-center gap-2 transition disabled:opacity-50"
                                    >
                                        {reagendando ? (
                                            <>
                                                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                <CalendarIcon size={16} /> Reagendar
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}